/**
 * 分组排序核心逻辑
 * - optimizeFunctions: 根据配置生成优化后的内部函数集合
 * - sortByGroupedPrimaryKeyLightCore: 轻量核心分组排序（每组一个代表值，不做组内排序）
 * - sortByGroupedPrimaryKeyFullCore: 完整核心分组排序（支持主值选择与组内排序）
 *
 * 该文件不直接暴露给业务侧，而是由上层 API 和工厂方法调用。
 */

import {
  IInternalSortOptions,
  IOptimizedSortFunctions,
  ISortConfig,
  TGroupedPrimaryKeyGroup,
} from './types';
import { convertToSortableValue } from './valueUtils';

/**
 * 根据配置生成优化后的函数集合，消除运行时类型判断，提升性能。
 *
 * 性能注意事项（按配置项划分）：
 * - groupKey / primaryKey / keyExtractor / valueExtractor:
 *   - 这些配置负责“取字段”，如果实现只是 `item => item.xxx` 或 `item => item[primaryKey]`，几乎没有额外性能负担。
 *   - 不建议在这里做聚合、深拷贝、正则等重计算逻辑，因为这些函数会在 O(n) 次遍历中被频繁调用。
 * - numericConverter:
 *   - 只对“匹配到主键的元素”做数值转换，平均调用次数 ≤ n，默认实现 `convertToSortableValue` 仅做轻量容错和转换。
 *   - 如果自定义的 numericConverter 中包含大量字符串处理或复杂校验，在大数据量场景下会明显拖慢整体排序。
 * - compareFn（分组层面的比较）:
 *   - 调用次数是 g 级别（分组数）排序比较，远少于 n；即便逻辑相对复杂，一般也不会成为主要瓶颈。
 * - options.groupOrderComparator（组内排序）:
 *   - 每个分组内部排序会调用比较器 O(k_i log k_i) 次，当某个分组很大（如上万条）且比较器逻辑复杂时，会显著放大总耗时。
 *   - 建议比较器只做简单的数值/字符串比较，将复杂值预先算好挂在元素上，比较函数只读这些字段，不做 I/O 或重计算。
 * - options.primaryValueSelector:
 *   - 每个分组调用一次（共 g 次），但内部可以写任意逻辑；如果在 selector 内对整组数据做线性扫描或复杂聚合，相当于在排序后再追加一段 O(n) 的自定义计算。
 *   - 建议主要在 matchedItems 上做有限次扫描，避免对整组 `group.items` 反复全量 filter/sort。
 *
 * @param config - 排序配置对象，包含主键、分组键、取值与比较相关的函数。
 */
export function optimizeFunctions<T>(
  config: ISortConfig<T>,
): IOptimizedSortFunctions<T> | null {
  const {
    primaryKey,
    groupKey,
    keyExtractor,
    valueExtractor,
    numericConverter,
    compareFn,
  } = config;

  // 如果没有传入 primaryKey，返回 null（表示不排序）
  if (!primaryKey) {
    return null;
  }

  // 优化分组键提取函数：消除运行时类型判断
  const optimizedGetGroupKey: (item: T) => string =
    typeof groupKey === 'string'
      ? // 字符串形式：直接访问属性，无需类型判断
      (item: T) => String((item as any)[groupKey] || '')
      : // 函数形式：直接使用，无需包装
      groupKey;

  // 优化主键值提取函数：消除运行时条件判断
  const optimizedGetItemKey: (item: T) => any = keyExtractor
    ? // 有 keyExtractor：直接使用
    keyExtractor
    : typeof primaryKey === 'string'
      ? // primaryKey 是字符串：直接访问属性
      (item: T) => (item as any)[primaryKey]
      : // primaryKey 是函数：直接使用
      primaryKey;

  // 优化主键匹配函数：根据配置生成最优匹配函数
  const optimizedIsPrimaryKeyMatch: (item: T) => boolean =
    typeof primaryKey === 'string'
      ? // primaryKey 是字符串：直接比较
      (item: T) => optimizedGetItemKey(item) === primaryKey
      : // primaryKey 是函数：比较函数返回值
      (item: T) => {
        const itemKey = optimizedGetItemKey(item);
        const primaryKeyValue = primaryKey(item);
        return itemKey === primaryKeyValue;
      };

  // 优化值提取函数：消除运行时条件判断
  const optimizedGetValue: (item: T) => any = valueExtractor
    ? // 有 valueExtractor：直接使用
    valueExtractor
    : // 无 valueExtractor：使用主键值
    optimizedGetItemKey;

  // 优化数值转换函数：直接使用，无需条件判断
  const optimizedConvertValue: (
    value: any,
  ) => { numeric: number; isValid: boolean; } =
    numericConverter || convertToSortableValue;

  // 优化比较函数：消除运行时条件判断
  const optimizedCompare: (
    a: TGroupedPrimaryKeyGroup<T>,
    b: TGroupedPrimaryKeyGroup<T>,
  ) => number = compareFn
    ? // 有自定义比较函数：直接使用
    compareFn
    : // 默认比较函数：内联实现，避免函数调用
    (a, b) => {
      if (a.primaryValue === b.primaryValue) {
        return a.order - b.order;
      }
      return b.primaryValue - a.primaryValue;
    };

  return {
    getGroupKey: optimizedGetGroupKey,
    getItemKey: optimizedGetItemKey,
    isPrimaryKeyMatch: optimizedIsPrimaryKeyMatch,
    getValue: optimizedGetValue,
    convertValue: optimizedConvertValue,
    compare: optimizedCompare,
  };
}

/**
 * 根据传入的优化函数集合，将一维数组按分组键拆分为分组结构，
 * 并为每个分组计算主值（primaryValue）和代表元素（selectedItem）。
 *
 * @param items - 原始数据数组
 * @param functions - 由 optimizeFunctions 生成的优化函数集合
 * @param collectMatchedItems - 是否收集 matchedItems（仅在需要 primaryValueSelector 时为 true）
 */
function buildGroupedPrimaryKeyGroups<T>(
  items: T[],
  functions: IOptimizedSortFunctions<T>,
  collectMatchedItems: boolean,
): TGroupedPrimaryKeyGroup<T>[] {
  const groups: TGroupedPrimaryKeyGroup<T>[] = [];
  const groupIndexMap = new Map<string, number>();

  items.forEach((item, index) => {
    const key = functions.getGroupKey(item);
    let groupIndex = groupIndexMap.get(key);
    if (groupIndex === undefined) {
      groupIndex = groups.length;
      groups.push({
        items: [],
        primaryValue: Number.NEGATIVE_INFINITY,
        order: index,
        hasValidValue: false,
        groupKey: key,
        matchedItems: collectMatchedItems ? [] : undefined,
      });
      groupIndexMap.set(key, groupIndex);
    }
    const group = groups[groupIndex];
    group.items.push(item);
    // 仅在首次遇到匹配主键的项时计算 primaryValue，避免多次覆盖
    const isMatched = functions.isPrimaryKeyMatch(item);
    if (isMatched && collectMatchedItems) {
      group.matchedItems?.push(item);
    }
    if (!collectMatchedItems && isMatched && !group.hasValidValue) {
      const value = functions.getValue(item);
      const { numeric, isValid } = functions.convertValue(value);
      group.primaryValue = numeric;
      group.hasValidValue = isValid;
      if (isValid) {
        group.selectedItem = item;
        group.selectedValue = numeric;
      }
    }
  });

  return groups;
}

/**
 * 轻量级核心排序函数
 * - 每个分组只计算一个代表值（默认：首个匹配 primaryKey 的元素的数值）
 * - 不支持主值选择器（primaryValueSelector）
 * - 不支持组内排序（groupOrderComparator）
 *
 * 适合对性能要求较高、且不需要组内复杂排序策略的场景。
 */
export function sortByGroupedPrimaryKeyLightCore<T>(
  items: T[],
  functions: IOptimizedSortFunctions<T>,
): T[] {
  // 先按分组键对元素进行分组，并为每一组计算主键的排序值
  const groups = buildGroupedPrimaryKeyGroups(items, functions, false);

  // 小数组快速路径：当分组数量 <= 10 时，使用插入排序（对小数组更快）
  const SMALL_ARRAY_THRESHOLD = 10;
  let sortedGroups: TGroupedPrimaryKeyGroup<T>[];

  if (groups.length <= SMALL_ARRAY_THRESHOLD) {
    // 使用插入排序对小数组进行排序
    sortedGroups = [...groups]; // 创建副本，避免修改原数组

    for (let i = 1;i < sortedGroups.length;i++) {
      const current = sortedGroups[i];
      let j = i - 1;

      // 将当前元素插入到已排序部分的正确位置
      while (j >= 0 && functions.compare(sortedGroups[j], current) > 0) {
        sortedGroups[j + 1] = sortedGroups[j];
        j -= 1;
      }
      sortedGroups[j + 1] = current;
    }
  } else {
    // 大数组使用原生 sort 方法
    sortedGroups = groups.sort(functions.compare);
  }

  // 预分配数组并直接赋值，比 concat 快得多
  const result: T[] = new Array(items.length);
  let index = 0;
  for (const group of sortedGroups) {
    for (const item of group.items) {
      result[index] = item;
      index += 1;
    }
  }
  return result;
}

/**
 * 完整版核心排序函数
 * - 在轻量分组与主值计算的基础上，支持：
 *   - 主值选择器（primaryValueSelector）：在分组完成后统一确定每组主值
 *   - 组内排序（groupOrderComparator）：根据上下文对每个分组内部进行排序
 */
export function sortByGroupedPrimaryKeyFullCore<T>(
  items: T[],
  functions: IOptimizedSortFunctions<T>,
  options?: IInternalSortOptions<T>,
): T[] {
  const shouldCollectMatchedItems = Boolean(options?.primaryValueSelector);

  // 先按分组键对元素进行分组，并为每一组计算主键的初始排序值
  const groups = buildGroupedPrimaryKeyGroups(
    items,
    functions,
    shouldCollectMatchedItems,
  );

  // 高级主值选择逻辑：在分组完成后统一处理
  if (shouldCollectMatchedItems) {
    const selector = options?.primaryValueSelector;
    groups.forEach((group) => {
      const matchedItems = group.matchedItems || [];
      if (selector && matchedItems.length) {
        const selection = selector({
          matchedItems,
          groupItems: group.items,
          groupKey: group.groupKey,
          groupIndex: group.order,
        });
        if (selection?.value !== undefined) {
          const { numeric, isValid } = functions.convertValue(selection.value);
          group.primaryValue = numeric;
          group.hasValidValue = isValid;
          if (isValid) {
            group.selectedValue = numeric;
            group.selectedItem = selection.item;
          }
        } else if (selection?.item) {
          const value = functions.getValue(selection.item);
          const { numeric, isValid } = functions.convertValue(value);
          group.primaryValue = numeric;
          group.hasValidValue = isValid;
          if (isValid) {
            group.selectedItem = selection.item;
            group.selectedValue = numeric;
          }
        }
      }
      if (!group.hasValidValue) {
        const fallbackItem = matchedItems[0];
        if (fallbackItem) {
          const value = functions.getValue(fallbackItem);
          const { numeric, isValid } = functions.convertValue(value);
          group.primaryValue = numeric;
          group.hasValidValue = isValid;
          if (isValid) {
            group.selectedItem = fallbackItem;
            group.selectedValue = numeric;
          }
        }
      }
    });
  }

  // 小数组快速路径：当分组数量 <= 10 时，使用插入排序（对小数组更快）
  const SMALL_ARRAY_THRESHOLD = 10;
  let sortedGroups: TGroupedPrimaryKeyGroup<T>[];

  if (groups.length <= SMALL_ARRAY_THRESHOLD) {
    // 使用插入排序对小数组进行排序
    sortedGroups = [...groups]; // 创建副本，避免修改原数组

    for (let i = 1;i < sortedGroups.length;i++) {
      const current = sortedGroups[i];
      let j = i - 1;

      // 将当前元素插入到已排序部分的正确位置
      while (j >= 0 && functions.compare(sortedGroups[j], current) > 0) {
        sortedGroups[j + 1] = sortedGroups[j];
        j -= 1;
      }
      sortedGroups[j + 1] = current;
    }
  } else {
    // 大数组使用原生 sort 方法
    sortedGroups = groups.sort(functions.compare);
  }

  // 组内排序：当提供 groupOrderComparator 时，对每个分组内部进行排序
  const groupComparator = options?.groupOrderComparator;
  if (groupComparator) {
    sortedGroups.forEach((group) => {
      group.items.sort((a, b) =>
        groupComparator(a, b, {
          groupKey: group.groupKey,
          groupIndex: group.order,
          groupItems: group.items,
          selectedItem: group.selectedItem,
          selectedValue: group.selectedValue,
        }),
      );
    });
  }

  // 预分配数组并直接赋值，比 concat 快得多
  const result: T[] = new Array(items.length);
  let index = 0;
  for (const group of sortedGroups) {
    for (const item of group.items) {
      result[index] = item;
      index += 1;
    }
  }
  return result;
}

